import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMobileOS } from '@/common/hooks/useMobileOS'
import { InstallWebAppSheet } from './installWebAppModal'
import Context from './pwaContext'
import InstallAppClickedAnalytics from '../analytics/events/pwa/InstallAppClickedAnalytics'
import DeviceAuthRequester from './DeviceAuthRequester'
import useAuthStore from '@/auth/authStore'
import useUserStore from '@/user/userStore'
import { USER_ID_TOKEN } from '@/common/constants'
import { useSignOut } from '@/auth/useSignOut'
import { PasskeyAuthRequiredModal, PasskeySetupRequiredModal } from './deviceAuthModals'
import { useDeviceAuthFlow } from './useDeviceAuthFlow'
import { hasStoredPasskey } from './webauthn'
import {
    clearDeferredInstallPrompt,
    getDeferredInstallPrompt,
    isPwaInstalled,
    promptDeferredInstall,
    subscribeInstallLifecycle,
} from './installPromptAdapter'
import { deriveInstallAvailability } from './installAvailability'

let swRefreshing = false

export function PwaProvider({ children }: { children: React.ReactNode }) {
    const [supportPwa, setSupportPwa] = useState(false)
    const [installed, setInstalled] = useState(isPwaInstalled())
    const authDetails = useAuthStore((s) => s.authDetails)
    const user = useUserStore((s) => s.user)
    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false)
    const os = useMobileOS()

    useEffect(() => {
        if (typeof window === 'undefined') return

        const updatePromptState = () => setSupportPwa(!!getDeferredInstallPrompt())

        updatePromptState()

        const unsubscribeInstallLifecycle = subscribeInstallLifecycle({
            onPromptAvailable: () => {
                updatePromptState()
            },
            onInstalled: () => {
                setInstalled(true)
                setSupportPwa(false)
                setIsInstallModalOpen(false)
            },
        })

        const onControllerChange = () => {
            if (swRefreshing) return
            // Only reload when we explicitly applied the update through the app
            if (!window.__sixPwaAppliedByApp) return
            swRefreshing = true
            window.location.reload()
        }

        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
        }

        const cleanup = () => {
            try {
                unsubscribeInstallLifecycle()
            } catch {
                // ignore
            }
            try {
                if ('serviceWorker' in navigator)
                    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
            } catch {
                // ignore
            }
        }

        return cleanup
    }, [])

    const promptInstall = useCallback(async () => {
        if (getDeferredInstallPrompt()) {
            try {
                InstallAppClickedAnalytics.track({ OS: os })
                const res = await promptDeferredInstall()
                setSupportPwa(!!getDeferredInstallPrompt())
                setInstalled(isPwaInstalled())
                return res
            } catch {
                setSupportPwa(!!getDeferredInstallPrompt())
                return null
            }
        }
        return null
    }, [os])

    const hide = useCallback(() => {
        try {
            clearDeferredInstallPrompt()
        } catch {
            // ignore
        }
        setSupportPwa(false)
    }, [])

    const availability = useMemo(
        () => deriveInstallAvailability({ os, isInstalled: installed, hasAndroidInstallPrompt: supportPwa }),
        [installed, os, supportPwa],
    )

    const available = availability.available

    const openInstallModal = () => {
        if (!available) return
        setIsInstallModalOpen(true)
    }

    const closeInstallModal = () => setIsInstallModalOpen(false)

    const isSessionAuthenticated =
        typeof window !== 'undefined' ? !!sessionStorage.getItem(USER_ID_TOKEN) || !!authDetails : !!authDetails
    const currentUserId = authDetails?.userId ?? authDetails?.username
    const hasAuthIdentity = !!currentUserId

    const signOut = useSignOut()
    const onManualSignOut = useCallback(() => signOut({ redirect: true, clearStorage: false }), [signOut])

    const registerPasskeyOptions = useMemo(() => {
        if (!currentUserId) return null

        const emailId =
            user?.emailId?.trim() ||
            (typeof authDetails?.username === 'string' && authDetails.username.includes('@')
                ? authDetails.username
                : currentUserId)

        const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || emailId

        return {
            userId: currentUserId,
            emailId,
            name,
        }
    }, [authDetails?.username, currentUserId, user?.emailId, user?.firstName, user?.lastName])

    const getRegisterPasskeyOptions = useCallback(() => registerPasskeyOptions, [registerPasskeyOptions])
    const hasPasskeyStored = hasStoredPasskey()
    const canRunDeviceAuth = isSessionAuthenticated && hasAuthIdentity && (hasPasskeyStored || !!registerPasskeyOptions)

    const {
        requestDeviceAuth,
        passkeySetupModalOpen,
        passkeySetupError,
        passkeyAuthModalOpen,
        passkeyAuthError,
        passkeyAuthWaiting,
        resolvePasskeySetupChoice,
        resolvePasskeyAuthChoice,
    } = useDeviceAuthFlow({
        userId: currentUserId,
        onManualSignOut,
        getRegisterPasskeyOptions,
    })

    return (
        <Context.Provider
            value={{
                available,
                supportPwa,
                promptInstall,
                hide,
                isInstallModalOpen,
                openInstallModal,
                closeInstallModal,
            }}
        >
            {children}
            {installed && (
                <>
                    <DeviceAuthRequester isAuthenticated={canRunDeviceAuth} requestDeviceAuth={requestDeviceAuth} />
                    <PasskeySetupRequiredModal
                        open={passkeySetupModalOpen}
                        error={passkeySetupError}
                        onSetup={() => resolvePasskeySetupChoice('setup')}
                        onSkip={() => resolvePasskeySetupChoice('skip')}
                    />
                    <PasskeyAuthRequiredModal
                        open={passkeyAuthModalOpen}
                        error={passkeyAuthError}
                        waiting={passkeyAuthWaiting}
                        onUsePasskey={() => resolvePasskeyAuthChoice('use-passkey')}
                        onLogout={() => resolvePasskeyAuthChoice('logout')}
                    />
                </>
            )}
            {available && <InstallWebAppSheet open={isInstallModalOpen} onDismiss={closeInstallModal} />}
        </Context.Provider>
    )
}

export default PwaProvider
